import React, { useEffect, useCallback, useRef, useState } from "react";
import { GridCallbackDetails, GridColDef, GridPaginationModel, GridRenderCellParams, GridRowModel, GridRowModes, GridRowParams, GridRowSelectionModel, GridSlotsComponent } from "@mui/x-data-grid";

import TableStyled from "../TableStyled";
// import { LinearProgress, debounce } from "@mui/material";

// import { LinearProgress, debounce } from "@mui/material";
export type PageInfo = {
    hasNextPage: boolean;
    hasPreviousPage: boolean;
    startCursor?: string | null;
    endCursor?: string | null;
};
type UncapitalizeObjectKeys<T> = {
    [K in keyof T as K extends string ? Uncapitalize<K> : never]: T[K];
};
export type FetchMoreCallbackArgs = Partial<{
    query: string | null;
    first: number | null;
    after: string | null;
    replace: boolean | null;
    hasNextPage: boolean;
    filterKey?: string;
    filterValue?: string | string[] | null;
}>;

interface DataGridState {
    page: number;
    pageSize: number;
    rowCount: number;
    columns: GridColDef[];
    rows: GridRowModel[];
}

/**
 * Check if string has a value
 *
 * @param value
 * @returns true if values exists false otherwise
 */
function hasValue(value: string): boolean {
    return value?.trim()?.length > 0;
    // ?? false; don't need false as it should be taken care of, the ?? was throwing an error due to the false being unreachable by definition
}


/**
 * Used to ensure object returned is initialized properly. The argment
 * {@link FetchMoreCallbackArgs.hasNextPage} is expected to be a boolean and not undefined,
 * despite the type lying.
 *
 * @param args
 * @returns
 */
function normalizeFetchMoreArgs(
    args: FetchMoreCallbackArgs
): FetchMoreCallbackArgs {
    return {
        ...args,
        hasNextPage: args.hasNextPage ? args.hasNextPage : false,
    };
}

interface PageTableSelectionProps {
    rowSelectionModel?: GridRowSelectionModel;
    onRowSelectionModelChange?: (
        rowSelectionModel: GridRowSelectionModel,
        details: GridCallbackDetails
    ) => void;
}

export type RenderToolbarProps = {
    searchQuery?: string;
    onSearch?: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onSearchClear?: () => void;
};

type TablePaginatedProps = {
    pageInfo?: PageInfo | undefined;
    loading: boolean;
    pageSize: number;
    columns: GridColDef[];
    rows: GridRowModel[];
    totalRows: number;
    filterParams?: { filterType: string, filter: string | null }
    slots?: Omit<UncapitalizeObjectKeys<Partial<GridSlotsComponent>>, 'loadingOverlay'>
    // renderToolbar?: (props: RenderToolbarProps) => void;
    renderToolbar?: (props: RenderToolbarProps) => void;
    toolbarComponents?: (props: any) => React.ReactNode | null;
    onFetchMore?: (options: FetchMoreCallbackArgs) => void;
    isRowSelectable?: (params: GridRowParams<any>) => boolean;
    getRowClassName?: (params: GridRowParams<any>) => string;
    selectionProps?: PageTableSelectionProps;
    disableBoxShadow?: boolean;
    checkboxSelection?: boolean;
    disableColumnSelector?: boolean;
    sortable?: boolean;
    autoHeight?: boolean;
    style?: any
}

export default function TablePaginated({
    pageInfo,
    loading,
    pageSize,
    selectionProps = {},
    columns, rows, totalRows, filterParams,
    slots,
    onFetchMore,
    isRowSelectable,
    getRowClassName,
    checkboxSelection,
    disableBoxShadow = false,
    renderToolbar: renderHeader,
    disableColumnSelector = true,
    sortable = false,
    autoHeight = false,
    style
}: TablePaginatedProps) {
    const paginationCursorsRef = useRef<{ [page: number]: string }>({});
    const [search, setSearch] = useState<{ query: string; hasQuery: boolean }>({
        query: "", hasQuery: false
    });
    const debounceRef = useRef<NodeJS.Timeout | null>(null);


    const [dataGridState, setDataGridState] = useState<DataGridState>({
        page: 0,
        pageSize: pageSize,
        rowCount: 0,
        columns: [],
        rows: [],
    });
    // Because the developer may chose to pass in a lambda directly on the props
    // we need to ensure the signature is unchanged after the initial render.
    // We'll memorize the function signature on first pass, and perform
    // argument/object normalization. This is required because a use
    // effect downstream uses the fetch more as a dependecy.
    const callbackFetchMore = useCallback(
        (args: FetchMoreCallbackArgs) => {
            onFetchMore?.(normalizeFetchMoreArgs(args))
        },
        []
    );
    useEffect(() => {
        setDataGridState((prevState) => ({
            ...prevState,
            columns: columns,
        }));
    }, [columns, loading]);

    useEffect(() => {
        setDataGridState((prevState) => ({
            ...prevState,
            rows,
        }));
    }, [rows]);

    useEffect(() => {
        // If we have a search string avoid pagination
        if (search.hasQuery) return;
        const nextCursor =
            paginationCursorsRef.current[dataGridState.page] ?? pageInfo?.endCursor;
        if (!loading && nextCursor) {
            if (paginationCursorsRef.current[dataGridState.page] !== nextCursor) {
                paginationCursorsRef.current[dataGridState.page] = nextCursor;
            }
        }
    }, [loading, search, dataGridState.page, pageInfo]);

    useEffect(() => {
        setDataGridState((prevState) => ({
            ...prevState,
            rowCount: totalRows >= 0 ? totalRows : prevState.rowCount,
        }));
    }, [totalRows]);

    const hasMounted = useRef(false);
    useEffect(() => {
        // Reset pagination the user removes the query from search input
        if (!search.hasQuery) {
            if (!hasMounted.current) {
                hasMounted.current = true;
                return;
            }
            // Need to set the page back to the first page (0) in the case the user
            // changed to other pages before using a search query.
            setDataGridState((prevState) => ({
                ...prevState,
                page: 0,
            }));

            // Get a fresh first page of data like the initial load
            callbackFetchMore({
                query: null,
                first: dataGridState.pageSize,
                after: null,
                replace: true,
                ...(filterParams?.filterType
                    ? { [filterParams.filterType]: filterParams.filter }
                    : {})
            });

            return;
        }
    }, [search, callbackFetchMore]);
    const fetchMoreSearches = async (searchText: string) => {
        if (!hasValue(searchText)) return;
        callbackFetchMore({
            query: searchText,
            first: pageSize,
            after: null,
        });
    };

    // Ensure we use the same function between component renders
    // const handleDebounceSearch = useCallback(
    //     debounce(fetchMoreSearches, 230),
    //     []
    // );
    const handleDebounceSearch = (searchText: string) => {
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }

        debounceRef.current = setTimeout(() => {
            fetchMoreSearches(searchText);
        }, 230);
    };

    const handleDataGridSearch = (searchQuery: string) => {
        if (!hasValue(searchQuery)) {
            setSearch({
                query: "",
                hasQuery: false,
            });

            return;
        }

        setSearch({
            query: searchQuery,
            // The DataGrid component has an issue when searching results locally. If the
            // user is on another page other than the first, queries come up blank. To temp
            // fix this problem we use the `hasQuery` flag to switch to pagination "server"
            // put the user on the first page, and return the number of records that match
            // the user query.
            hasQuery: true,
        });

        // Only send the search request when the user has stop typing for 300ms.
        handleDebounceSearch(searchQuery);
    };

    // When page changes:
    const handlePageChange = (model: GridPaginationModel, details: GridCallbackDetails) => {
        const isFirstPage = model.page === 0
        const isNextPage = model.page > dataGridState.page; // compare to previous page
        const isPrevPage = model.page < dataGridState.page;
        let afterCursor;
        // const afterCursor = isFirstPage ? undefined : paginationCursorsRef.current[model.page - 1]
        setDataGridState((prevState) => ({
            ...prevState,
            page: model.page,
        }));
        if (isNextPage) {
            afterCursor = pageInfo?.endCursor
        } else if (isPrevPage) {
            afterCursor = paginationCursorsRef.current[model.page - 1] ?? null
        }
        // Prevent over-fetching if query page size doesn't match table page size
        if (isFirstPage || afterCursor || pageInfo?.hasNextPage) {

            callbackFetchMore({
                first: dataGridState.pageSize,
                after: afterCursor,
                ...(filterParams?.filterType
                    ? { [filterParams.filterType]: filterParams.filter }
                    : {})
            });
        }

    };

    // When new pageInfo arrives (from parent GraphQL query):
    useEffect(() => {
        if (pageInfo?.endCursor) {
            paginationCursorsRef.current[dataGridState.page] = pageInfo.endCursor;
        }
    }, [pageInfo, dataGridState.page]);


    const handlePageSizeChange = (pageSize: number) => {
        setDataGridState((prevState) => ({
            ...prevState,
            pageSize,
        }));

        callbackFetchMore({
            first: dataGridState.pageSize,
            after:
                paginationCursorsRef.current[dataGridState.page - 1] ??
                pageInfo?.endCursor,
        });
    };

    return (
        <>
            {renderHeader &&
                renderHeader({
                    searchQuery: search.query,
                    onSearch: (event) => handleDataGridSearch(event.target.value),
                    onSearchClear: () => handleDataGridSearch(""),
                })
            }

            <TableStyled
                style={{ minHeight: 52, ...style }}
                loading={loading}
                autoHeight
                disableColumnResize
                // NOTE: KEY ADDED TO FORCE A RELOAD/RE-RENDER OF THE TABLE IN CASE THERE IS AN ISSUE WITH THE ROWS
                // key={dataGridState.rowCount}
                getRowHeight={autoHeight ? () => 'auto' : undefined}
                pagination
                paginationMode={"server"}

                // paginationMode={search.hasQuery ? "server" : "client"}
                paginationModel={{
                    page: search.hasQuery ? 0 : dataGridState.page,
                    pageSize
                }}
                rows={rows}
                rowCount={
                    search.hasQuery
                        ? dataGridState.rows?.length ?? 0
                        : dataGridState.rowCount
                }
                isRowSelectable={isRowSelectable}
                getRowClassName={getRowClassName}
                columns={dataGridState.columns}
                pageSizeOptions={[pageSize]}
                slots={{
                    ...slots,
                }}
                slotProps={{
                    loadingOverlay: {
                        variant: "linear-progress"
                    }
                }}
                onPaginationModelChange={handlePageChange}
                // onPageSizeChange={handlePageSizeChange}
                {...selectionProps}
                disableBoxShadow={disableBoxShadow}
                disableColumnSelector={disableColumnSelector}
                disableColumnMenu
                checkboxSelection={checkboxSelection}
                disableRowSelectionOnClick={true}
            />
        </>
    );
}